import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import style from "./index.module.scss";
import CloseIcon from "../../shared/icons/CloseIcon";
import { Driver, File } from "../../../services";
import { requestErrorDisplay } from "../../../utils";
import Loading from "../../shared/Loading/Loading";
import cn from "classnames";
import DocumentNewIcon from "../../shared/icons/DocumentNewIcon";
import EyeIcon from "../../shared/icons/EyeIcon";
import UnloadIcon from "../../shared/icons/UnloadIcon";
import PhotoViewer from "../../partners/components/PhotoViewer";
import { SquarePen } from "lucide-react";
import InputCustom from "../../shared/forms/input-custom";
import InputMask from "react-input-mask";
import { toast } from "react-toastify";

const formatDocumentType = (contentType) => {
  const mimeMap = {
    "application/pdf": "pdf",
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/webp": "webp",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  };

  return mimeMap[contentType] || contentType?.split("/")[1];
};

const DriverInfoModal = ({ isOpen, onClose, driverId, onDriverUpdate, isEditMode, setIsEditMode }) => {
  const { t } = useTranslation();
  const [isLoading, setLoading] = useState(false);
  const [driverInfo, setDriverInfo] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loadingStates, setLoadingStates] = useState({});
  const [loadingStatesDownload, setLoadingStatesDownload] = useState({});
  const [isPhotoViewerOpen, setIsPhotoViewerOpen] = useState(false);
  const [imgUrl, setImgUrl] = useState("");
  const [currentDocType, setCurrentDocType] = useState("");
  const [cachedFiles, setCachedFiles] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [patronymic, setPatronymic] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [iin, setIin] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [issuedBy, setIssuedBy] = useState("");
  const [issuedDate, setIssuedDate] = useState("");
  const [fileUpdates, setFileUpdates] = useState({});

  const [errors, setErrors] = useState({
    name: "",
    surname: "",
    phoneNumber: "",
    idNumber: "",
    iin: "",
    issuedBy: "",
    issuedDate: "",
  });

  useEffect(() => {
    if (isOpen && driverId) {
      fetchDriverInfo();
      fetchDriverDocuments();
    } else if (!isOpen) {
      setIsEditMode(false);
      setFileUpdates({});
      setErrors({
        name: "",
        surname: "",
        phoneNumber: "",
        idNumber: "",
        iin: "",
        issuedBy: "",
        issuedDate: "",
      });
    }
  }, [isOpen, driverId]);

  const fetchDriverInfo = async () => {
    setLoading(true);
    try {
      const response = await Driver.getDriverInfo(driverId);
      const data = response.data;
      setDriverInfo(data);
      setName(data.name || "");
      setSurname(data.surname || "");
      setPatronymic(data.patronymic || "");
      setPhoneNumber(data.phoneNumber ? `+${data.phoneNumber}` : "");
      setIin(data.iin || "");
      setIdNumber(data.idNumber || "");
      setIssuedBy(data.issuedBy || "");
      setIssuedDate(data.issuedDate ? formatDateForInput(data.issuedDate) : "");
    } catch (error) {
      requestErrorDisplay(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverDocuments = async () => {
    try {
      const response = await File.getDocList(driverId);
      const filteredData = response.data.filter((doc) => doc.type !== 8);
      setDocuments(filteredData);
    } catch (error) {
      console.error("Error loading driver documents:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatIssuedDateForApi = (date) => {
    const [day, month, year] = date.split("-");
    if (!day || !month || !year) return "";
    const dateObj = new Date(year, month - 1, day);
    return dateObj.toISOString();
  };

  const setLoadingViewer = (docId, isLoading) => {
    setLoadingStates((prev) => ({ ...prev, [docId]: isLoading }));
  };

  const setLoadingDownload = (docId, isLoading) => {
    setLoadingStatesDownload((prev) => ({ ...prev, [docId]: isLoading }));
  };

  const openViewPhoto = (fileId) => {
    if (cachedFiles[fileId]) {
      const file = cachedFiles[fileId];
      setCurrentDocType(fileId);

      if (file.contentType === "application/pdf") {
        window.open(file.url, "_blank");
      } else {
        setImgUrl(file.url);
        setIsPhotoViewerOpen(true);
      }
      return;
    }

    setLoadingViewer(fileId, true);

    File.getDocById(fileId)
      .then((response) => {
        setLoadingViewer(fileId, false);
        const file = new Blob([response.data], {
          type: response.data.type,
        });

        const fileURL = URL.createObjectURL(file);
        setCachedFiles((prev) => ({
          ...prev,
          [fileId]: {
            blob: file,
            url: fileURL,
            type: response.data.type,
            contentType: response.data.type,
          },
        }));
        setCurrentDocType(fileId);

        if (response.data.type === "application/pdf") {
          window.open(fileURL, "_blank");
        } else {
          setImgUrl(fileURL);
          setIsPhotoViewerOpen(true);
        }
      })
      .catch((error) => {
        setLoadingViewer(fileId, false);
        console.log("Произошла ошибка, попробуйте позже", error);
        toast.error(t("common.genericError"));
      });
  };

  const onDownload = (fileId) => {
    const doc = documents.find((x) => x.fileId === fileId);
    if (!doc) {
      toast.error(t("orderInfo.driverDocumentViewer.docNotFound"));
      return;
    }

    if (cachedFiles[doc.fileId]) {
      const file = cachedFiles[doc.fileId];
      const downloadElement = document.createElement("a");
      downloadElement.href = file.url;
      downloadElement.download = `${doc.typeName}.${formatDocumentType(file.type)}`;
      downloadElement.click();
      return;
    }

    setLoadingDownload(fileId, true);
    File.getDocById(doc.fileId)
      .then((response) => {
        const fileBlob = new Blob([response.data], {
          type: response.data.type,
        });

        const fileURL = URL.createObjectURL(fileBlob);

        setCachedFiles((prev) => ({
          ...prev,
          [doc.fileId]: { blob: fileBlob, url: fileURL, type: response.data.type },
        }));

        const downloadElement = document.createElement("a");
        downloadElement.href = fileURL;
        downloadElement.download = `${doc.typeName}.${formatDocumentType(response.data.type)}`;
        downloadElement.click();
      })
      .catch((error) => {
        console.log("Произошла ошибка, попробуйте позже", error);
        toast.error(t("common.genericError"));
      })
      .finally(() => {
        setLoadingDownload(fileId, false);
      });
  };

  const handlePhotoViewerClose = () => {
    setIsPhotoViewerOpen(false);
  };

  const restrictDateInput = (value, setter) => {
    if (!value) {
      setter("");
      return "";
    }

    setter(value);

    if (errors.issuedDate && value.length === 10) {
      setErrors((prev) => ({ ...prev, issuedDate: "" }));
    }

    const numericValue = value.replace(/[^\d-]/g, "");
    const parts = numericValue.split("-");
    let [day, month, year] = ["", "", ""];

    if (parts.length > 0) day = parts[0].replace(/\D/g, "");
    if (parts.length > 1) month = parts[1].replace(/\D/g, "");
    if (parts.length > 2) year = parts[2].replace(/\D/g, "");

    if (day.length === 2) {
      const dayNum = parseInt(day, 10);
      if (dayNum < 1) day = "01";
      if (dayNum > 31) day = "31";
    }

    if (month.length === 2) {
      const monthNum = parseInt(month, 10);
      if (monthNum < 1) month = "01";
      if (monthNum > 12) month = "12";
    }

    if (year.length === 4) {
      const yearNum = parseInt(year, 10);
      if (yearNum < 1900) year = "1900";
    }

    let formattedDate = day;
    if (parts.length > 1) formattedDate += "-" + month;
    if (parts.length > 2) formattedDate += "-" + year;

    setter(formattedDate);
    return formattedDate;
  };

  const validateForm = () => {
    const newErrors = {
      name: "",
      surname: "",
      phoneNumber: "",
      idNumber: "",
      iin: "",
      issuedBy: "",
      issuedDate: "",
    };

    let isValid = true;

    if (!name.trim()) {
      newErrors.name = t("driversCreatePage.validation.invalidName");
      isValid = false;
    }

    if (!surname.trim()) {
      newErrors.surname = t("driversCreatePage.validation.invalidLastName");
      isValid = false;
    }

    const cleanPhone = phoneNumber.replace(/\D/g, "");
    if (phoneNumber && (cleanPhone.length < 8 || cleanPhone.length > 15)) {
      newErrors.phoneNumber = t("driversCreatePage.validation.invalidPhone");
      isValid = false;
    }

    if (!idNumber || idNumber.length !== 9) {
      newErrors.idNumber = t("driversCreatePage.validation.invalidIdNumber");
      isValid = false;
    }

    if (iin && iin.length !== 12) {
      newErrors.iin = t("driversCreatePage.validation.invalidIin");
      isValid = false;
    }

    if (!issuedBy.trim()) {
      newErrors.issuedBy = t("driversCreatePage.validation.invalidIssuedBy");
      isValid = false;
    }

    if (!issuedDate || issuedDate.length !== 10) {
      newErrors.issuedDate = t("driversCreatePage.validation.invalidIssuedDate");
      isValid = false;
    }

    setErrors(newErrors);

    if (!isValid) {
      Object.values(newErrors).forEach((error) => {
        if (error) {
          toast.error(error);
        }
      });
    }

    return isValid;
  };

  const handleEditToggle = () => {
    if (isEditMode) {
      setName(driverInfo.name || "");
      setSurname(driverInfo.surname || "");
      setPatronymic(driverInfo.patronymic || "");
      setPhoneNumber(driverInfo.phoneNumber ? `+${driverInfo.phoneNumber}` : "");
      setIin(driverInfo.iin || "");
      setIdNumber(driverInfo.idNumber || "");
      setIssuedBy(driverInfo.issuedBy || "");
      setIssuedDate(driverInfo.issuedDate ? formatDateForInput(driverInfo.issuedDate) : "");
      setFileUpdates({});
      setErrors({
        name: "",
        surname: "",
        phoneNumber: "",
        idNumber: "",
        iin: "",
        issuedBy: "",
        issuedDate: "",
      });
    }
    setIsEditMode(!isEditMode);
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      const updateData = {};

      if (name.trim()) updateData.name = name.trim();
      if (surname.trim()) updateData.surname = surname.trim();
      if (patronymic.trim()) updateData.patronymic = patronymic.trim();
      if (phoneNumber) updateData.phoneNumber = phoneNumber.replace(/\D/g, "");
      if (iin) updateData.iin = iin;
      if (idNumber) updateData.idNumber = idNumber;
      if (issuedBy.trim()) updateData.issuedBy = issuedBy.trim();
      if (issuedDate) updateData.issuedDate = formatIssuedDateForApi(issuedDate);

      await Driver.updateDriverByExpeditor(driverId, updateData);

      for (const [key, file] of Object.entries(fileUpdates)) {
        if (file) {
          const [typeStr, sideStr] = key.split("-");
          const formData = new FormData();
          formData.append("driverId", driverId);
          formData.append("side", parseInt(sideStr));
          formData.append("file", file);
          formData.append("type", parseInt(typeStr));
          await File.uploadDocumentDriver(formData);
        }
      }

      toast.success(t("driversCreatePage.toast.updateSuccess"));

      await fetchDriverInfo();
      await fetchDriverDocuments();

      if (onDriverUpdate) {
        onDriverUpdate();
      }

      setIsEditMode(false);
      setFileUpdates({});
    } catch (error) {
      requestErrorDisplay(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDocumentEdit = (doc) => {
    const input = document.createElement("input");
    input.type = "file";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const key = `${doc.type}-${doc.side}`;
        setFileUpdates((prev) => ({ ...prev, [key]: file }));
        toast.info(
          t("driversCreatePage.toast.fileSelected"),
        );
      }
    };
    input.click();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={style.modal_overlay} onClick={onClose}>
        <div className={style.modal_container} onClick={(e) => e.stopPropagation()}>
          {isLoading ? (
            <Loading />
          ) : driverInfo ? (
            <div className={style.content}>
              <div className={style.field_col_2}>
                <div className={style.field}>
                  <span className={style.field_label}>{t("driversCreatePage.name.label")} *</span>
                  {isEditMode ? (
                    <InputCustom
                      placeholder={t("driversCreatePage.name.placeholder")}
                      defaultValue={name}
                      onChangeValue={(value) => {
                        setName(value);
                        if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));
                      }}
                      className={errors.name ? style.input_error : ""}
                      errorText={errors?.name}
                    />
                  ) : (
                    <div className={style.field_value}>{driverInfo.name || "-"}</div>
                  )}
                </div>
                <div className={style.field}>
                  <span className={style.field_label}>{t("driversCreatePage.lastName.label")} *</span>
                  {isEditMode ? (
                    <InputCustom
                      placeholder={t("driversCreatePage.lastName.placeholder")}
                      defaultValue={surname}
                      onChangeValue={(value) => {
                        setSurname(value);
                        if (errors.surname) setErrors((prev) => ({ ...prev, surname: "" }));
                      }}
                      className={errors.surname ? style.input_error : ""}
                      errorText={errors?.surname}
                    />
                  ) : (
                    <div className={style.field_value}>{driverInfo.surname || "-"}</div>
                  )}
                </div>
              </div>

              <div className={style.field_col_2}>
                <div className={style.field}>
                  <span className={style.field_label}>{t("driversCreatePage.phone.label")}</span>
                  {isEditMode ? (
                    <InputCustom
                      placeholder="+"
                      defaultValue={phoneNumber}
                      onChangeValue={(value) => {
                        let formattedValue = value;
                        if (!formattedValue.startsWith('+')) {
                          formattedValue = '+' + formattedValue.replace(/\+/g, '');
                        }
                        formattedValue = '+' + formattedValue.substring(1).replace(/\D/g, '');
                        
                        setPhoneNumber(formattedValue);
                        if (errors.phoneNumber) setErrors((prev) => ({ ...prev, phoneNumber: "" }));
                      }}
                      maxLength={16}
                      className={errors.phoneNumber ? style.input_error : ""}
                      errorText={errors.phoneNumber}
                    />
                  ) : (
                    <div className={style.field_value}>
                      {driverInfo.phoneNumber ? `+${driverInfo.phoneNumber}` : "-"}
                    </div>
                  )}
                </div>
                <div className={style.field}>
                  <span className={style.field_label}>{t("driversCreatePage.patronymic.label")}</span>
                  {isEditMode ? (
                    <InputCustom
                      placeholder={t("driversCreatePage.patronymic.placeholder")}
                      defaultValue={patronymic}
                      onChangeValue={setPatronymic}
                    />
                  ) : (
                    <div className={style.field_value}>{driverInfo.patronymic || "-"}</div>
                  )}
                </div>
              </div>

              <div className={style.field_col_2}>
                <div className={style.field}>
                  <span className={style.field_label}>{t("driversCreatePage.iin.label")}</span>
                  {isEditMode ? (
                    <InputCustom
                      defaultValue={iin}
                      onChangeValue={(value) => {
                        setIin(value);
                        if (errors.iin) setErrors((prev) => ({ ...prev, iin: "" }));
                      }}
                      placeholder={t("driversCreatePage.iin.placeholder")}
                      maxLength={12}
                      className={errors.iin ? style.input_error : ""}
                      errorText={errors.iin}
                    />
                  ) : (
                    <div className={style.field_value}>{driverInfo.iin || "-"}</div>
                  )}
                </div>
                <div className={style.field}>
                  <span className={style.field_label}>{t("driversCreatePage.idNumber.label")} *</span>
                  {isEditMode ? (
                    <InputCustom
                      defaultValue={idNumber}
                      onChangeValue={(value) => {
                        setIdNumber(value);
                        if (errors.idNumber) setErrors((prev) => ({ ...prev, idNumber: "" }));
                      }}
                      placeholder={t("driversCreatePage.idNumber.placeholder")}
                      maxLength={9}
                      className={errors.idNumber ? style.input_error : ""}
                      errorText={errors?.idNumber}
                    />
                  ) : (
                    <div className={style.field_value}>{driverInfo.idNumber || "-"}</div>
                  )}
                </div>
              </div>

              <div className={style.field_col_2}>
                <div className={style.field}>
                  <span className={style.field_label}>{t("driversCreatePage.issuedDate.label")} *</span>
                  {isEditMode ? (
                    <InputMask
                      value={issuedDate}
                      maskChar={null}
                      mask="99-99-9999"
                      onChange={(event) => restrictDateInput(event.target.value, setIssuedDate)}
                      disabled={false}
                    >
                      {(inputProps) => (
                        <InputCustom
                          {...inputProps}
                          defaultValue={issuedDate}
                          onChangeValue={(value) => restrictDateInput(value, setIssuedDate)}
                          placeholder={t("driversCreatePage.issuedDate.placeholder")}
                          maxLength={10}
                          className={errors.issuedDate ? style.input_error : ""}
                          errorText={errors?.issuedDate}
                        />
                      )}
                    </InputMask>
                  ) : (
                    <div className={style.field_value}>{formatDate(driverInfo.issuedDate) || "-"}</div>
                  )}
                </div>
                <div className={style.field}>
                  <span className={style.field_label}>{t("driversCreatePage.issuedBy.label")} *</span>
                  {isEditMode ? (
                    <InputCustom
                      defaultValue={issuedBy}
                      onChangeValue={(value) => {
                        setIssuedBy(value);
                        if (errors.issuedBy) setErrors((prev) => ({ ...prev, issuedBy: "" }));
                      }}
                      placeholder={t("driversCreatePage.issuedBy.placeholder")}
                      className={errors.issuedBy ? style.input_error : ""}
                      errorText={errors?.issuedBy}
                    />
                  ) : (
                    <div className={style.field_value}>{driverInfo.issuedBy || "-"}</div>
                  )}
                </div>
              </div>

              {documents && documents.length > 0 && (
                <div>
                  <h4 className={style.field_section}>{t("orderInfo.driverDocumentViewer.title")}</h4>

                  <div className={style.documentsList}>
                    {documents.map((doc, index) => (
                      <div key={index} className={style.documentItem}>
                        <div className={style.documentItem_icon}>
                          <DocumentNewIcon />
                        </div>
                        <div className={style.documentItem_content}>
                          <div className={style.documentItem_name}>
                            {doc?.typeName}.{formatDocumentType(doc?.contentType)}
                          </div>
                          <div className={style.documentItem_size}>{doc?.sideName}</div>
                        </div>

                        {isEditMode && (
                          <SquarePen
                            color="#6D7885"
                            size={22}
                            style={{ cursor: "pointer" }}
                            onClick={() => handleDocumentEdit(doc)}
                          />
                        )}

                        {loadingStatesDownload[doc?.fileId] ? (
                          <Loading inBlock marginBottom={0} />
                        ) : (
                          <UnloadIcon onClick={() => onDownload(doc.fileId)} style={{ cursor: "pointer" }} />
                        )}

                        {loadingStates[doc?.fileId] ? (
                          <Loading inBlock marginBottom={0} />
                        ) : (
                          <EyeIcon onClick={() => openViewPhoto(doc?.fileId)} style={{ cursor: "pointer" }} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isEditMode && (
                <div className={style.button_container}>
                  <button className={style.buttonCancel} onClick={handleEditToggle} disabled={isSaving}>
                    {t("driversCreatePage.buttons.cancel") || "Отмена"}
                  </button>
                  <button
                    className={cn(style.button_upload, {
                      [style.button_upload_loading]: isSaving,
                    })}
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {!isSaving && <span>{t("common.save")}</span>}
                    {isSaving && <Loading inBlock marginBottom={0} />}
                  </button>
                </div>
              )}
            </div>
          ) : null}

          {!isEditMode && (
            <button
              onClick={handleEditToggle}
              className={style.buttonEdit}
              title={t("driversCreatePage.buttons.edit") || "Редактировать"}
            >
              <SquarePen size={20} />
            </button>
          )}

          <button onClick={onClose} className={style.buttonClose}>
            <CloseIcon />
          </button>
        </div>
      </div>

      {isPhotoViewerOpen && (
        <PhotoViewer
          imgUrl={imgUrl}
          onClose={handlePhotoViewerClose}
          onDownload={() => onDownload(currentDocType)}
          ShowBtnDownload
        />
      )}
    </>
  );
};

export default DriverInfoModal;
